import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axiosInstance";
import { toast } from "react-hot-toast";

// Get All Careers
export const getAllCareers = createAsyncThunk(
    "career/getAllCareers",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get("/carrier");
            return response.data;
        } catch (error) {
            const message = error.response?.data?.error || "Failed to fetch applications.";
            toast.error(message);
            return rejectWithValue(message);
        }
    }
);

// Delete Career
export const deleteCareer = createAsyncThunk(
    "career/deleteCareer",
    async (id, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(`/carrier/${id}`);
            toast.success("Application deleted successfully!");
            return id;
        } catch (error) {
            const message = error.response?.data?.error || "Failed to delete application.";
            toast.error(message);
            return rejectWithValue(message);
        }
    }
);

// Update Career Status
export const updateCareerStatus = createAsyncThunk(
    "career/updateStatus",
    async ({ id, formData }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/carrier/${id}`, formData);
            toast.success("Status updated successfully!");
            return response.data.data;
        } catch (error) {
            const message = error.response?.data?.error || "Failed to update status.";
            toast.error(message);
            return rejectWithValue(message);
        }
    }
);

// Send Schedule Email
export const sendScheduleEmail = createAsyncThunk(
    "career/sendScheduleEmail",
    async (formData, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post("/send_schedule", formData);
            toast.success(response.data.message || "Email sent successfully!");
            return response.data;
        } catch (error) {
            const message = error.response?.data?.error || "Failed to send email invite.";
            toast.error(message);
            return rejectWithValue(message);
        }
    }
);

const careerSlice = createSlice({
    name: "career",
    initialState: {
        loading: false,
        applications: [],
        count: 0,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Get All
            .addCase(getAllCareers.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAllCareers.fulfilled, (state, action) => {
                state.loading = false;
                state.applications = action.payload.data;
                state.count = action.payload.count;
            })
            .addCase(getAllCareers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // Update Status
            .addCase(updateCareerStatus.fulfilled, (state, action) => {
                const index = state.applications.findIndex((app) => app._id === action.payload._id);
                if (index !== -1) {
                    state.applications[index] = action.payload;
                }
            })
            // Delete
            .addCase(deleteCareer.fulfilled, (state, action) => {
                state.applications = state.applications.filter((app) => app._id !== action.payload);
                state.count -= 1;
            })
            // Send Schedule Email
            // Send Schedule Email
            .addCase(sendScheduleEmail.pending, (state) => {
                // We avoid setting state.loading = true here to prevent a full-page spinner.
                state.error = null;
            })
            .addCase(sendScheduleEmail.fulfilled, (state, action) => {
                // Handle both structures: action.payload as candidate OR action.payload.data as candidate
                const candidate = action.payload?.data || action.payload;

                if (candidate && (candidate.id || candidate._id)) {
                    const index = state.applications.findIndex((app) =>
                        (app._id || app.id)?.toString() === (candidate._id || candidate.id)?.toString()
                    );

                    if (index !== -1) {
                        // Update the candidate in the list
                        state.applications[index] = {
                            ...state.applications[index],
                            ...candidate,
                            status: candidate.status || 'Interviewing' // Ensure status is updated
                        };
                    }
                }
            })
            .addCase(sendScheduleEmail.rejected, (state, action) => {
                state.error = action.payload;
            });
    },
});

export default careerSlice.reducer;
